import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ParsedFilterBlock } from 'src/types/marketplace';
import { Button } from 'src/uikit/Button';
import { Checkbox } from 'src/uikit/Form/Checkbox';
import { ChevronRightIcon } from 'src/uikit/icons/ChevronRightIcon';
import { SearchInput } from 'src/uikit/SearchInput';

import s from './FilterBlock.module.scss';

const MocksRu = {
  reset: 'Сбросить',
  collapse: 'Свернуть',
  expand: 'Показать еще',
  searchTags: 'Поиск по тегам',
  nothingFoundTitle: 'Ничего не найдено',
  nothingFoundDescription: 'Попробуйте изменить запрос',
};

type Props = Omit<ParsedFilterBlock, 'filterKey'> & {
  filterKey: string;
  collapsable?: boolean;
};

export function FilterBlock({
  filterKey,
  options,
  search,
  collapsable,
}: Props) {
  const { register } = useFormContext();

  const [searchValue, setSearchValue] = useState<string>('');
  const [collapsed, setCollapsed] = useState<boolean>(Boolean(collapsable));

  const isBlockCollapsable = !searchValue && collapsable && options.length > 10;

  const displayedCheckboxes = useMemo(
    () =>
      searchValue.trim()
        ? options.filter(({ name }) =>
            name.toLowerCase().includes(searchValue.trim().toLowerCase()),
          )
        : options,
    [options, searchValue],
  );

  const toggleCollapse = () => {
    setCollapsed((old) => !old);
  };

  return (
    <>
      <div>
        {search && (
          <div className={s.searchWrapper}>
            <SearchInput
              value={searchValue}
              onValueChange={setSearchValue}
              placeholder={MocksRu.searchTags}
            />
          </div>
        )}

        {displayedCheckboxes.length ? (
          <>
            <div
              className={cn(s.listWrapper, {
                [s.collapsed]: isBlockCollapsable ? collapsed : false,
              })}
            >
              {displayedCheckboxes.map((checkbox) => (
                <Checkbox
                  key={checkbox.id}
                  value={checkbox.id}
                  name={filterKey}
                  label={checkbox.name}
                  counter={checkbox.count}
                  className={s.checkBox}
                  register={register}
                  errors={{}}
                  required={false}
                  fullWidth
                />
              ))}
            </div>

            {isBlockCollapsable && (
              <Button
                className={cn(s.collapseButton, !collapsed && s.revertIcon)}
                variant={Button.variants.Tertiary}
                onClick={toggleCollapse}
                analyticsOptions={{
                  action: 'click',
                  clickZone: 'body',
                  clickElement: 'button',
                  clickContent: collapsed ? MocksRu.expand : MocksRu.collapse,
                  uniqueId: 'marketplace-body-category-change',
                  transitionType: 'inside-link',
                }}
              >
                <span>{collapsed ? MocksRu.expand : MocksRu.collapse}</span>
                <ChevronRightIcon />
              </Button>
            )}
          </>
        ) : (
          <div className={s.nothingFound}>
            <p>{MocksRu.nothingFoundTitle}</p>
            <p>{MocksRu.nothingFoundDescription}</p>
          </div>
        )}
      </div>
    </>
  );
}
